import type { FastDriveScanInput, FastDriveScanResult, ScanCandidate, ScanStatus } from './fast-drive-scan.js';

/** A short, human-readable one-liner the model reads to pick its next move. */
export function guidanceFor(status: ScanStatus, input: FastDriveScanInput, count: number): string {
  switch (status) {
    case 'ready':
      return 'One confident target — act on `target.locator`. If `query` came from a variable, cache the binding, not the literal.';
    case 'ambiguous':
      // Coordinate-probe has no query to narrow — the ambiguity is usually the
      // app rendering the same control twice (e.g. a pinned copy + an off-screen
      // strip copy sharing one testid). The agent pointed at a specific in-view
      // pixel, so steer it to the visible instance rather than query knobs.
      return input.at
        ? `This durable locator matches more than one element — the app likely renders the control twice (one may be off-screen). You pointed at the in-view one: act with the \`in-view\` candidate (add \`.first()\` if it's first in DOM, or scope to its container).`
        : `${count} viable targets. Narrow by adding \`targetRole\`/\`targetText\`/\`relation\`, or pick one by its \`anchor\`, then re-scan the chosen one. No scroll performed.`;
    case 'partial':
      return 'Scan hit its time budget before verifying. Candidates below are UNVERIFIED — narrow the query, or escalate to browser_snapshot / browser_scout.';
    case 'not_found':
      return input.at
        ? `Nothing actionable at (${input.at.x}, ${input.at.y}) — the point may be empty, covered by an overlay, or the coordinates are stale (re-screenshot after any scroll).`
        : `No control matched "${input.query}". The text may be normalized differently, be in another view/tab, or need a broader term — try browser_snapshot or browser_scout.`;
  }
}

function candidateLine(c: ScanCandidate, idx?: number): string {
  const tag = idx !== undefined ? `${idx + 1}. ` : '';
  const where = c.anchor ? ` in ${c.anchor}` : '';
  const flags = [
    c.unique === true ? 'unique' : c.unique === false ? 'not-unique' : 'unverified',
    c.inViewport ? 'in-view' : 'off-screen',
    c.enabled ? null : 'disabled',
  ]
    .filter(Boolean)
    .join(', ');
  return `${tag}${c.role} "${c.name}"${where}  →  ${c.locator}  [${flags}]`;
}

/**
 * Compact, token-disciplined rendering of a scan result for the agent. Always
 * leads with `status` and ends with `guidance` (the model's next move).
 */
export function formatScanResultText(res: FastDriveScanResult): string {
  const lines: string[] = [];
  if (res.status === 'ready' && res.target) {
    lines.push('status: ready');
    lines.push(candidateLine(res.target));
    if (res.ref) lines.push('(ephemeral ref — do NOT cache this locator)');
  } else if (res.status === 'ambiguous' || res.status === 'partial') {
    lines.push(
      `status: ${res.status} (${res.candidates.length} candidate${res.candidates.length === 1 ? '' : 's'})`
    );
    res.candidates.forEach((c, i) => lines.push(candidateLine(c, i)));
  } else {
    lines.push('status: not_found');
  }
  lines.push(`guidance: ${res.guidance}`);
  return lines.join('\n');
}
